/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useContext } from 'react';
import InputForm from 'components/TradeHelper/InputForm';
import FollowupQuestionsDisplay from 'components/TradeHelper/FollowupQuestionsDisplay';
import { useWebSocketFetcher } from 'hooks/useWebSocketFetcher';
import { iFollowupItem } from 'interfaces/iFollowupItem';
import { iStreamedResponse } from 'interfaces/iStreamedResponse';
import StreamDisplay from 'components/TradeHelper/StreamDisplay';
import { Grid } from '@mui/material';
import DeleteCommonModal from 'components/Monitor/Modals/DeleteModal';
import { Modal } from '@mui/material';
import { GlobalContext } from 'providers/GlobalProvider';
import CasesAPI from 'api/CasesAPI';
import { useSnackbar } from 'notistack';
import format from 'date-fns/format';
import { LoadingFeedback } from 'components/Common/LoadingModal';

import { useUpdateProduct } from 'api/bitedb/products/products.hooks';
import { getProductById } from 'api/bitedb/products/products.service';

import { useRunProductScreening } from 'api/bitedb/screeningHistory/screening.hooks';

const apiUrl = process.env.REACT_APP_PYAPI_URL as string;

interface ClassificationProps {
  product: any;
  handleClose?: () => void;
  handleRefresh?: any;
}

const Classification: React.FC<ClassificationProps> = ({
  product,
  handleClose,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();
  const updateProductMutation = useUpdateProduct();
  const runProductScreeningMutation = useRunProductScreening();

  const dt = new Date();
  const dtWithoutTz = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  );
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');

  const { streamData, isStreaming, connectWebSocket } =
    useWebSocketFetcher(apiUrl);

  const [userProductDesc, setUserProductDesc] = useState<string>(
    product.name || product.description,
  );
  const [country, setCountry] = useState<string>(
    product.country_of_origin || '',
  );
  const [followupItems, setFollowupItems] = useState<iFollowupItem[]>([]);
  const [clfId, setClfId] = useState<string | null>(null);
  const [cmdtDesc, setCmdtDesc] = useState<string | null>(null);
  const [HTSCode, setHTSCode] = useState<string | null>(
    product.hts_code || null,
  );
  const [explanatoryNotes, setExplanatoryNotes] = useState<string | null>(null);
  const [sectionNotes, setSectionNotes] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState<number>(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandedCross, setIsExpandedCross] = useState(false);
  const [isExpandedSecNotes, setIsExpandedSecNotes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savingTicket, setSavingTicket] = useState(false);
  const [ticketId, setTicketID] = useState('');
  const [owner, setOwner] = useState('');
  const [crossLinks, setCrossLinks] = useState<string[]>([]);
  // License Checking
  const [userHasClassify, setUserHasClassify] = useState(false);
  const requiredTiers = ['starter', 'pro', 'business', 'enterprise'];

  const checkForClassify = () => {
    if (
      requiredTiers.some((role) =>
        cur_user.selected_team?.attributes?.tier.includes(role),
      )
    ) {
      setUserHasClassify(true);
    }
  };

  useEffect(() => {
    checkForClassify();
    setOwner(cur_user.basics?.email || '');
  }, []);

  useEffect(() => {
    setHTSCode(product.hts_code || '');
    setUserProductDesc(product.name || '');
    setCountry(product.country_of_origin || '');
  }, [product]);

  // Process stream
  useEffect(() => {
    streamData.forEach((chunk) => {
      try {
        const classificationReq: iStreamedResponse = JSON.parse(chunk);
        const {
          followup_items,
          clf_id,
          hts_code,
          cmdt_desc,
          explanatory_notes,
          section_chapter_notes,
          cross_data_links,
        } = classificationReq;
        if (hts_code) {
          setHTSCode(hts_code);
        }
        if (cmdt_desc) {
          setCmdtDesc(cmdt_desc);
        }
        if (explanatory_notes) {
          setExplanatoryNotes(explanatory_notes);
        }
        if (section_chapter_notes) {
          setSectionNotes(section_chapter_notes);
        }
        if (cross_data_links) {
          setCrossLinks(cross_data_links);
        }
        if (clf_id && clf_id.trim() !== '') {
          setClfId((prev) => (prev === clf_id ? prev : clf_id));
        }

        if (followup_items && followup_items.length > 0) {
          setFollowupItems((prev) => {
            const existingQuestions = new Set(
              prev.map((item) => item.question),
            );
            const newItems = followup_items.filter(
              (item) => !existingQuestions.has(item.question),
            );
            return [...prev, ...newItems];
          });
        }
      } catch (err) {
        console.warn('Invalid JSON chunk in useEffect:', chunk, err);
      }
    });
  }, [streamData]);

  // Handle default stages
  useEffect(() => {
    if (followupItems.length > 0) {
      setCurrentStage(2);
    }
    if (cmdtDesc && HTSCode && explanatoryNotes) {
      setCurrentStage(3);
    }
  }, [followupItems, cmdtDesc, HTSCode]);

  const submitClassification = async (
    product_desc: string,
    followups: iFollowupItem[],
  ) => {
    const clf = {
      clf_id: clfId,
      product_desc: product_desc,
      followup_items: followups,
    };
    connectWebSocket('/ws/classify', clf);
  };

  const handleSubmit = async (inputValue: string) => {
    if (inputValue !== '') {
      setUserProductDesc(inputValue);
      setCurrentStage(1);
      setIsLoading(true);
      try {
        await submitClassification(inputValue, followupItems);
        setIsLoading(false);
      } catch (error) {
        console.error(error);
      }
    } else {
      enqueueSnackbar('Please input something to classify', {
        variant: 'error',
      });
    }
  };

  const handleFollowupSubmit = (responses: iFollowupItem[]) => {
    if (userProductDesc !== '') {
      setIsLoading(true);
      setFollowupItems(responses);
      submitClassification(userProductDesc, responses);
      setIsLoading(false);
    } else {
      enqueueSnackbar('Please input something to classify', {
        variant: 'error',
      });
    }
  };

  const handleReset = () => {
    setCurrentStage(0);
    setHTSCode('');
    setCmdtDesc('');
    setFollowupItems([]);
    setUserProductDesc('');
    setTicketID('');
  };

  const saveTicket = async () => {
    setSavingTicket(true);

    const updatedProd = await getProductById(product.id);
    if (!updatedProd) {
      enqueueSnackbar('Product was not found', {
        variant: 'error',
      });
      return;
    }

    updatedProd.hts_code = HTSCode || undefined;

    const result = await updateProductMutation.mutateAsync({
      id: product.id,
      productData: updatedProd,
    });

    if (result) {
      enqueueSnackbar('Product updated successfully', {
        variant: 'success',
      });
    }

    const payload = {
      databaseId: product.database_id || '',
      databaseProductId: updatedProd?.database_product_id ?? undefined,
      productId: updatedProd?.id ?? undefined,
      initiatedBy: cur_user?.basics?.id,
    };

    runProductScreeningMutation.mutate(payload, {
      onSuccess: (response) => {
        const successes = Array.isArray(response?.result?.results)
          ? response.result.results.filter((r: any) => r.success)
          : null;
        const hasSuccess = successes ? successes.length > 0 : true;

        enqueueSnackbar(
          hasSuccess
            ? 'Screening started successfully'
            : 'Screening request accepted with warnings',
          { variant: hasSuccess ? 'success' : 'warning' },
        );
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.response?.data?.message || 'Failed to start screening',
          { variant: 'error' },
        );
      },
    });

    if (handleClose) {
      handleClose();
    }
  };

  const formatExplanatoryNote = (rawNotes: string) => {
    const cleanedNotes = rawNotes
      .replace(/[\[\]"]/g, '')
      .replace(/,(\s)?$/gm, '')
      .replace(/^°/gm, '');

    const lines = cleanedNotes
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const result: JSX.Element[] = [];
    let bulletItems: string[] = [];

    const flushBullets = () => {
      if (bulletItems.length > 0) {
        result.push(
          <ul
            key={`ul-${result.length}`}
            style={{ margin: '0.25rem 0', paddingLeft: '1.25rem' }}
          >
            {bulletItems.map((item, i) => (
              <li
                key={i}
                style={{ marginBottom: '0.25rem' }}
                dangerouslySetInnerHTML={{
                  __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                }}
              />
            ))}
          </ul>,
        );
        bulletItems = [];
      }
    };

    lines.forEach((line, index) => {
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      const isHeading =
        /^\*\*/.test(line) || (/^[A-Z\s]+$/.test(line) && line.length < 80);
      const isBullet = /^[-•*]|\(\w\)|^\(\d+\)|^\d+\)/.test(line);

      if (isHeading) {
        flushBullets();
        result.push(
          <h2
            key={`h-${index}`}
            style={{ margin: '0.5rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      } else if (isBullet) {
        bulletItems.push(formatted);
      } else {
        flushBullets();
        result.push(
          <p
            key={`p-${index}`}
            style={{ margin: '0.25rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      }
    });

    flushBullets();

    return result;
  };

  return (
    <>
      <section className='modal-right' style={{ minHeight: 0 }}>
        <Grid container>
          <Grid item xs={12}>
            <div className='bill-row'>
              <div className='bill-containers'>
                <div
                  className={`bill-container-body ${
                    isExpanded || isExpandedCross ? 'expanded' : ''
                  }`}
                  style={{ alignItems: 'flex-start', gap: '10px' }}
                >
                  <div className='screening-tabs-container'>
                    <div className='screening-tabs-inner'>
                      <div className={'screening-tab-btn'}>
                        <div className={'screening-tab-btn-text-selected'}>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='18'
                            height='18'
                            viewBox='0 0 18 18'
                            fill='none'
                            style={{ marginRight: '5px' }}
                          >
                            <path
                              d='M15.75 15.75L11.2501 11.25M12.75 7.5C12.75 10.3995 10.3995 12.75 7.5 12.75C4.6005 12.75 2.25 10.3995 2.25 7.5C2.25 4.6005 4.6005 2.25 7.5 2.25C10.3995 2.25 12.75 4.6005 12.75 7.5Z'
                              stroke='#4895EF'
                              strokeWidth='1.5'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                          HTS Classification - {product.name}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='disclaimer-text'>
                    The 10-digit HTS classification provided is for general, educational and planning  purposes only and not for purposes of filing and entry. Unless you are the importer of record, the specific tariff classification to be applied to a specific entry of merchandise is to be determined, supervised and controlled by the licensed customs broker. Users other than the importer of record must consult a licensed customs broker as classification for Customs entry purposes can only be determined by a licensed customs broker.
                  </div>
                  {currentStage === 0 && (
                    <InputForm
                      onSubmit={handleSubmit}
                      isStreaming={isStreaming}
                      initialValue={product.description || product.name || ''}
                    />
                  )}
                  {currentStage === 1 && (
                    <div className='classify'>
                      <div className='classify-header'>
                        Product Name / Product Description
                      </div>
                      <div className='classify-cont'>
                        <div className='classify-cont-sec'>
                          <div className='classify-cont-sec-div'>
                            <textarea
                              placeholder={userProductDesc}
                              className='classify-cont-sec-input'
                              rows={1}
                              onInput={(e) => {
                                const target = e.target as HTMLTextAreaElement; // type assertion
                                target.style.height = 'auto';
                                target.style.height = `${target.scrollHeight}px`;
                              }}
                            >
                              {userProductDesc}
                            </textarea>
                          </div>
                        </div>
                      </div>
                      <LoadingFeedback size='small' />
                      <div className='classify-footer'>
                        <div className='classify-footer-btn-loading'>
                          <div className='classify-footer-btn-div'>
                            Reasoning...
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {currentStage === 2 && (
                    <FollowupQuestionsDisplay
                      followupItems={followupItems}
                      isStreaming={isStreaming}
                      onSubmitFollowup={handleFollowupSubmit}
                      userProductDesc={userProductDesc}
                    />
                  )}
                  {currentStage === 3 && (
                    <>
                      <div
                        style={{
                          paddingTop: '10px',
                          color: 'var(--Text-Main, var(--text-primary))',
                          fontSize: '14px',
                          fontWeight: '600',
                        }}
                      >
                        <div>
                          <h2
                            style={{
                              color: 'var(--Text-Main, var(--text-primary))',
                              fontSize: '14px',
                              fontWeight: '600',
                            }}
                          >
                            Product Description:{' '}
                          </h2>
                          {cmdtDesc && (
                            <h2
                              style={{
                                color: 'var(--Text-Main, var(--text-primary))',
                                fontSize: '14px',
                                fontWeight: '400',
                              }}
                            >
                              {cmdtDesc}
                            </h2>
                          )}
                        </div>

                        <div>
                          <h2
                            style={{
                              color: 'var(--Text-Main, var(--text-primary))',
                              fontSize: '14px',
                              fontWeight: '600',
                            }}
                          >
                            HTS Code:{' '}
                          </h2>
                          {HTSCode && (
                            <h2
                              style={{
                                color: 'var(--Text-Main, var(--text-primary))',
                                fontSize: '14px',
                                fontWeight: '400',
                              }}
                            >
                              {HTSCode}
                            </h2>
                          )}
                        </div>
                        <div>
                          <h2
                            style={{
                              color: 'var(--Text-Main, var(--text-primary))',
                              fontSize: '14px',
                              fontWeight: '600',
                            }}
                          >
                            Section and Chapter Notes:{' '}
                          </h2>
                          {sectionNotes && (
                            <div
                              className={`explanatory_notes ${
                                !isExpandedSecNotes ? 'expanded' : ''
                              }`}
                            >
                              {formatExplanatoryNote(sectionNotes)}
                            </div>
                          )}
                          {sectionNotes && (
                            <div
                              className='expl-notes-btn'
                              onClick={() =>
                                setIsExpandedSecNotes(!isExpandedSecNotes)
                              }
                            >
                              {!isExpandedSecNotes ? 'Read More' : 'Read Less'}
                            </div>
                          )}
                        </div>

                        {/* <div>
                          <h2
                            style={{
                              color: 'var(--Text-Main, var(--text-primary))',
                              fontSize: '14px',
                              fontWeight: '600',
                            }}
                          >
                            Explanatory Notes:{' '}
                          </h2>
                          {explanatoryNotes && (
                            <div
                              className={`explanatory_notes ${
                                !isExpanded ? 'expanded' : ''
                              }`}
                            >
                              {formatExplanatoryNote(explanatoryNotes)}
                            </div>
                          )}
                          {explanatoryNotes && (
                            <div
                              className='expl-notes-btn'
                              onClick={() => setIsExpanded(!isExpanded)}
                              style={{ marginBottom: '10px' }}
                            >
                              {!isExpanded ? 'Read More' : 'Read Less'}
                            </div>
                          )}
                        </div> */}
                        <div>
                          <h2
                            style={{
                              color: 'var(--Text-Main, var(--text-primary))',
                              fontSize: '14px',
                              fontWeight: '600',
                              paddingTop: '10px',
                            }}
                          >
                            CROSS Rulings:{' '}
                          </h2>
                          {crossLinks.length > 0 && (
                            <p
                              className={`class-ticket-cross-${
                                !isExpandedCross ? 'expanded' : ''
                              }`}
                            >
                              {crossLinks.map((url, index) => (
                                <React.Fragment key={index}>
                                  <a
                                    href={url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='class-ticket-cross-rules'
                                  >
                                    {url}
                                  </a>
                                  <br />
                                </React.Fragment>
                              ))}
                            </p>
                          )}
                          {crossLinks.length > 5 && (
                            <div
                              className='expl-notes-btn'
                              onClick={() =>
                                setIsExpandedCross(!isExpandedCross)
                              }
                            >
                              {!isExpandedCross ? 'Read More' : 'Read Less'}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className='classify-footer'>
                        <div
                          className='classify-footer-btn'
                          onClick={saveTicket}
                        >
                          <div className='classify-footer-btn-divs'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='18'
                              height='18'
                              viewBox='0 0 18 18'
                              fill='none'
                            >
                              <path
                                d='M5.25 2.25V4.8C5.25 5.22004 5.25 5.43006 5.33175 5.59049C5.40365 5.73161 5.51839 5.84635 5.65951 5.91825C5.81994 6 6.02996 6 6.45 6H11.55C11.97 6 12.1801 6 12.3405 5.91825C12.4816 5.84635 12.5964 5.73161 12.6683 5.59049C12.75 5.43006 12.75 5.22004 12.75 4.8V3M12.75 15.75V10.95C12.75 10.53 12.75 10.3199 12.6683 10.1595C12.5964 10.0184 12.4816 9.90365 12.3405 9.83175C12.1801 9.75 11.97 9.75 11.55 9.75H6.45C6.02996 9.75 5.81994 9.75 5.65951 9.83175C5.51839 9.90365 5.40365 10.0184 5.33175 10.1595C5.25 10.3199 5.25 10.53 5.25 10.95V15.75M15.75 6.99411V12.15C15.75 13.4101 15.75 14.0402 15.5048 14.5215C15.289 14.9448 14.9448 15.289 14.5215 15.5048C14.0402 15.75 13.4101 15.75 12.15 15.75H5.85C4.58988 15.75 3.95982 15.75 3.47852 15.5048C3.05516 15.289 2.71095 14.9448 2.49524 14.5215C2.25 14.0402 2.25 13.4101 2.25 12.15V5.85C2.25 4.58988 2.25 3.95982 2.49524 3.47852C2.71095 3.05516 3.05516 2.71095 3.47852 2.49524C3.95982 2.25 4.58988 2.25 5.85 2.25H11.0059C11.3728 2.25 11.5562 2.25 11.7288 2.29145C11.8819 2.32819 12.0282 2.3888 12.1624 2.47104C12.3138 2.5638 12.4435 2.69352 12.7029 2.95294L15.0471 5.29706C15.3065 5.55648 15.4362 5.6862 15.529 5.83757C15.6112 5.97178 15.6718 6.1181 15.7086 6.27115C15.75 6.44378 15.75 6.62723 15.75 6.99411Z'
                                stroke='#fff'
                                strokeWidth='1.66667'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                            Save
                          </div>
                        </div>
                        <div
                          className='classify-footer-btn'
                          onClick={handleReset}
                        >
                          <div className='classify-footer-btn-divs'>
                            New Search
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div
                className='bill-container-fit-screens-classify'
                style={{ height: '100vh' }}
              >
                {currentStage === 0 ? (
                  <div className='classify-results'>
                    Enter information to see results. The more information you
                    provide, the more accurate the results will be.
                  </div>
                ) : (
                  <div className='classify-results-card-list'>
                    <StreamDisplay streamData={streamData} ticket={false} />
                  </div>
                )}

                <div className='classify-container-footer'>
                  <div
                    className='classify-container-footer-clear'
                    onClick={handleClose}
                  >
                    <div className='classify-container-footer-clear-text'>
                      X Close
                    </div>
                  </div>
                  <div
                    className='classify-container-footer-clear'
                    onClick={handleReset}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                    >
                      <path
                        d='M15.75 7.5C15.75 7.5 14.2463 5.45116 13.0246 4.22868C11.803 3.0062 10.1148 2.25 8.25 2.25C4.52208 2.25 1.5 5.27208 1.5 9C1.5 12.7279 4.52208 15.75 8.25 15.75C11.3273 15.75 13.9237 13.6907 14.7362 10.875M15.75 7.5V3M15.75 7.5H11.25'
                        stroke='var(--text-classify)'
                        strokeWidth='1.66667'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                    <div className='classify-container-footer-clear-text'>
                      Clear
                    </div>
                  </div>
                  {HTSCode && (
                    <div className='classify-footer-btn' onClick={saveTicket}>
                      <div className='classify-footer-btn-div'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='18'
                          height='18'
                          viewBox='0 0 18 18'
                          fill='none'
                        >
                          <path
                            d='M5.25 2.25V4.8C5.25 5.22004 5.25 5.43006 5.33175 5.59049C5.40365 5.73161 5.51839 5.84635 5.65951 5.91825C5.81994 6 6.02996 6 6.45 6H11.55C11.97 6 12.1801 6 12.3405 5.91825C12.4816 5.84635 12.5964 5.73161 12.6683 5.59049C12.75 5.43006 12.75 5.22004 12.75 4.8V3M12.75 15.75V10.95C12.75 10.53 12.75 10.3199 12.6683 10.1595C12.5964 10.0184 12.4816 9.90365 12.3405 9.83175C12.1801 9.75 11.97 9.75 11.55 9.75H6.45C6.02996 9.75 5.81994 9.75 5.65951 9.83175C5.51839 9.90365 5.40365 10.0184 5.33175 10.1595C5.25 10.3199 5.25 10.53 5.25 10.95V15.75M15.75 6.99411V12.15C15.75 13.4101 15.75 14.0402 15.5048 14.5215C15.289 14.9448 14.9448 15.289 14.5215 15.5048C14.0402 15.75 13.4101 15.75 12.15 15.75H5.85C4.58988 15.75 3.95982 15.75 3.47852 15.5048C3.05516 15.289 2.71095 14.9448 2.49524 14.5215C2.25 14.0402 2.25 13.4101 2.25 12.15V5.85C2.25 4.58988 2.25 3.95982 2.49524 3.47852C2.71095 3.05516 3.05516 2.71095 3.47852 2.49524C3.95982 2.25 4.58988 2.25 5.85 2.25H11.0059C11.3728 2.25 11.5562 2.25 11.7288 2.29145C11.8819 2.32819 12.0282 2.3888 12.1624 2.47104C12.3138 2.5638 12.4435 2.69352 12.7029 2.95294L15.0471 5.29706C15.3065 5.55648 15.4362 5.6862 15.529 5.83757C15.6112 5.97178 15.6718 6.1181 15.7086 6.27115C15.75 6.44378 15.75 6.62723 15.75 6.99411Z'
                            stroke='#fff'
                            strokeWidth='1.66667'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                        Save
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
        <Modal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
        >
          <DeleteCommonModal
            handleClose={() => setIsDeleteModalOpen(false)}
            title='Delete search?'
            paragraph='This will permanently remove this search from your group list.'
          />
        </Modal>
      </section>
    </>
  );
};

export default Classification;
